/**
 * Analytics Integration
 *
 * Centralized analytics tracking with PostHog, Google Analytics 4, and Hotjar
 */

import posthog from 'posthog-js';

// Environment variables
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const HOTJAR_SITE_ID = import.meta.env.VITE_HOTJAR_SITE_ID as string | undefined;

// Track initialization state
let isInitialized = false;

/**
 * Initialize analytics providers
 * Should be called once at app startup
 */
export function initAnalytics(): void {
  if (isInitialized) return;

  // Initialize PostHog
  if (POSTHOG_KEY && POSTHOG_HOST) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture page leaves
      capture_pageleave: true,
      // Disable session recording by default (can be enabled per user)
      disable_session_recording: true,
      // Respect Do Not Track
      respect_dnt: true,
      // Persistence
      persistence: 'localStorage+cookie',
      // Bootstrap with feature flags
      bootstrap: {
        distinctID: undefined,
        featureFlags: {},
      },
      // Loaded callback
      loaded: (ph) => {
        // In development, enable debug mode
        if (import.meta.env.DEV) {
          ph.debug();
        }
        console.log('[Analytics] PostHog initialized');
      },
    });
  } else {
    console.warn('[Analytics] PostHog not configured - VITE_POSTHOG_KEY or VITE_POSTHOG_HOST missing');
  }

  // Initialize Google Analytics 4
  if (GA4_MEASUREMENT_ID) {
    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    }
    gtag('js', new Date());
    gtag('config', GA4_MEASUREMENT_ID, {
      // Anonymize IP for GDPR compliance
      anonymize_ip: true,
      // Enable enhanced measurement
      enhanced_measurement: true,
    });

    // Expose gtag globally
    window.gtag = gtag;
    console.log('[Analytics] Google Analytics 4 initialized');
  } else {
    console.warn('[Analytics] GA4 not configured - VITE_GA4_MEASUREMENT_ID missing');
  }

  // Initialize Hotjar
  if (HOTJAR_SITE_ID) {
    // Initialize hj function
    window.hj =
      window.hj ||
      function (...args: unknown[]) {
        (window.hj.q = window.hj.q || []).push(args);
      };
    window._hjSettings = {
      hjid: parseInt(HOTJAR_SITE_ID, 10),
      hjsv: 6,
    };

    // Load Hotjar script
    const hjScript = document.createElement('script');
    hjScript.async = true;
    hjScript.src = `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`;
    document.head.appendChild(hjScript);

    console.log('[Analytics] Hotjar initialized');
  } else {
    console.warn('[Analytics] Hotjar not configured - VITE_HOTJAR_SITE_ID missing');
  }

  isInitialized = true;
}

/**
 * Identify a user for analytics
 */
export function identifyUser(
  userId: string,
  properties?: {
    email?: string;
    name?: string;
    plan?: string;
    workspaceId?: string;
    [key: string]: unknown;
  }
): void {
  // PostHog identification
  if (POSTHOG_KEY) {
    posthog.identify(userId, properties);
  }

  // GA4 user ID
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('config', GA4_MEASUREMENT_ID, {
      user_id: userId,
    });
    if (properties) {
      window.gtag('set', 'user_properties', properties);
    }
  }
}

/**
 * Reset analytics (on logout)
 */
export function resetAnalytics(): void {
  if (POSTHOG_KEY) {
    posthog.reset();
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  // PostHog event
  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  }

  // GA4 event
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', eventName, properties);
  }
}

/**
 * Track page view (usually automatic, but can be called manually for SPAs)
 */
export function trackPageView(path?: string): void {
  const pagePath = path || window.location.pathname;

  // PostHog pageview
  if (POSTHOG_KEY) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: pagePath,
    });
  }

  // GA4 pageview
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

/**
 * Check if a feature flag is enabled (PostHog)
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!POSTHOG_KEY) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Get feature flag value (PostHog)
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  if (!POSTHOG_KEY) return undefined;
  return posthog.getFeatureFlag(flagKey);
}

/**
 * Set user opt-out preference
 */
export function setOptOut(optOut: boolean): void {
  if (POSTHOG_KEY) {
    if (optOut) {
      posthog.opt_out_capturing();
    } else {
      posthog.opt_in_capturing();
    }
  }
}

/**
 * Check if user has opted out
 */
export function hasOptedOut(): boolean {
  if (!POSTHOG_KEY) return true;
  return posthog.has_opted_out_capturing();
}

// Re-export shared analytics events from @forgenetwork/types
// This provides consistent event naming across client and server
export { ClientAnalyticsEvents as AnalyticsEvents } from '@forgenetwork/types';
export type { ClientAnalyticsEventName, AnalyticsEventProperties } from '@forgenetwork/types';

// Type declarations for global analytics
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    hj: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings: { hjid: number; hjsv: number };
  }
}
